var m = require('mithril');
var prop = require('mithril/stream');
var loaders = require('../../layout/components/loaders');
var verticalListItem = require('../../components/verticalListItem');
var paginationWithSearch = require('../../data/components/paginationWithSearch');

var CLASS_LABELS = {
  BacnetServer: 'BACnet Hosts',
  BacnetDevice: 'BACnet Devices',
  LontalkCnip: 'LonTalk CNIP',
  LontalkNetwork: 'LonTalk Networks',
  LontalkDevice: 'LonTalk Devices',
  ModbusTcp: 'Modbus TCP Hosts',
  ModbusDevice: 'Modbus Devices',
  RestHttp: 'HTTP Hosts',
  RestResource: 'REST Resources'
};

var DEVICE_CLASSES_REGEX = /Device$/;

function ensureRidString(rid) {
  if (!rid) return rid;
  var str = rid.toString();
  return str[0] === '#' ? str : ('#' + str);
}

function ridForRequest(rid) {
  var str = ensureRidString(rid);
  return str ? str.slice(1) : str;
}

var deviceTree = {};

deviceTree.oninit = function (vnode) {
  var ctrl = this;
  ctrl.attrs = vnode.attrs || {};
  ctrl.selectedNode = vnode.attrs.selectedNode || prop(null);
  ctrl.loaderKey = ctrl.attrs.loader || 'background';

  ctrl.hosts = [];
  ctrl.hostsByClass = {};
  ctrl.levelStack = [];
  ctrl.recordIndex = {};
  ctrl.nodeCache = {};

  ctrl.newNode = prop(false);
  ctrl.dataToShow = [];

  ctrl.currentRecords = [];
  ctrl.currentSearchFields = ['name'];
  ctrl.currentPlaceholder = 'Search';
  ctrl.loading = prop(true);
  ctrl.error = prop(null);

  ctrl.isCategory = function (record) {
    return record && record.__type === 'category';
  };

  ctrl.isSelectable = function (record) {
    if (!record || !record['@class']) return false;
    if (Array.isArray(ctrl.attrs.selectableClasses) && ctrl.attrs.selectableClasses.length) {
      return ctrl.attrs.selectableClasses.indexOf(record['@class']) !== -1;
    }
    if (typeof ctrl.attrs.isSelectable === 'function') {
      return ctrl.attrs.isSelectable(record);
    }
    if (record['@class'] === 'RestResource') return true;
    return DEVICE_CLASSES_REGEX.test(record['@class']);
  };

  ctrl.filterChild = function (child) {
    if (!child) return false;
    if (child.deleted === true) return false;
    return true;
  };

  ctrl.keyForRecord = function (record) {
    if (!record) return null;
    if (record.__cacheKey) return record.__cacheKey;
    if (record['@rid']) return ensureRidString(record['@rid']);
    return null;
  };

  ctrl.classToUrl = function (cls) {
    if (!cls) return '';
    if (ctrl.attrs.classToUrl && ctrl.attrs.classToUrl[cls]) {
      return ctrl.attrs.classToUrl[cls];
    }
    return cls.toLowerCase();
  };

  ctrl.labelForClass = function (cls) {
    if (!cls) return 'Unknown';
    if (ctrl.attrs.classLabels && ctrl.attrs.classLabels[cls]) {
      return ctrl.attrs.classLabels[cls];
    }
    return CLASS_LABELS[cls] || cls;
  };

  ctrl.cleanForSelection = function (record) {
    if (!record) return null;
    var clean = Object.assign({}, record);
    clean['@rid'] = ensureRidString(clean['@rid']);
    clean.path = ctrl.buildSelectionPath(record);
    delete clean.__cacheKey;
    delete clean.__category;
    delete clean.__hasChildren;
    delete clean.__levelType;
    delete clean.__parentKey;
    delete clean.__type;
    delete clean.__label;
    return clean;
  };

  ctrl.buildSelectionPath = function (record) {
    var rootLabel = ctrl.attrs.rootLabel || 'Home';
    var crumbs = ctrl.levelStack
      .map(function (level, idx) {
        return level && level.label
          ? level.label
          : (idx === 0 ? rootLabel : '');
      })
      .filter(Boolean);
    if (crumbs.length && crumbs[0] === rootLabel) {
      crumbs = crumbs.slice(1);
    }
    var recordLabel = ctrl.displayName(record);
    if (recordLabel && (!crumbs.length || crumbs[crumbs.length - 1] !== recordLabel)) {
      crumbs.push(recordLabel);
    }
    return crumbs.join(' / ');
  };

  ctrl.storeRecord = function (record) {
    var key = ctrl.keyForRecord(record);
    if (!key) return;
    ctrl.recordIndex[key] = record;
  };

  ctrl.decorateRecord = function (record, options) {
    if (!record) return null;
    var decorated = Object.assign({}, record);
    decorated['@rid'] = ensureRidString(decorated['@rid']);
    decorated['@class'] = decorated['@class'] || (options && options.className);
    decorated.__levelType = options && options.levelType ? options.levelType : 'node';
    if (options && options.categoryName) decorated.__category = options.categoryName;
    if (options && options.parentKey) decorated.__parentKey = options.parentKey;
    if (options && typeof options.hasChildren !== 'undefined') decorated.__hasChildren = options.hasChildren;
    
    decorated.label = options && options.label ? options.label :
      (decorated.name || decorated.label || ctrl.labelForClass(decorated['@class']));
    ctrl.storeRecord(decorated);
    return decorated;
  };

  ctrl.buildCategories = function () {
    var categories = Object.keys(ctrl.hostsByClass)
      .map(function (className) {
        var hosts = ctrl.hostsByClass[className] || [];
        return {
          __cacheKey: 'category:' + className,
          __type: 'category',
          className: className,
          label: ctrl.labelForClass(className),
          count: hosts.length
        };
      });
    categories.sort(function (a, b) {
      return a.label.localeCompare(b.label);
    });
    return categories;
  };

  ctrl.prepareLevel = function (level) {
    ctrl.currentRecords = Array.isArray(level.records) ? level.records : [];
    ctrl.currentSearchFields = Array.isArray(level.searchFields) && level.searchFields.length
      ? level.searchFields
      : ['name'];
    ctrl.currentPlaceholder = level.placeholder || 'Search';
    ctrl.newNode(true);
  };

  ctrl.pushLevel = function (level) {
    ctrl.levelStack.push(level);
    ctrl.prepareLevel(level);
  };

  ctrl.trimLevelStackTo = function (count) {
    if (count < 1) count = 1;
    ctrl.levelStack = ctrl.levelStack.slice(0, count);
  };

  ctrl.goToLevel = function (idx) {
    if (idx < 0 || idx >= ctrl.levelStack.length) return;
    ctrl.levelStack = ctrl.levelStack.slice(0, idx + 1);
    var level = ctrl.levelStack[ctrl.levelStack.length - 1];
    ctrl.prepareLevel(level);
  };

  ctrl.fetchNode = function (record) {
    var key = ctrl.keyForRecord(record);
    if (!key) return Promise.resolve(null);
    if (ctrl.nodeCache[key]) {
      return Promise.resolve(ctrl.nodeCache[key]);
    }
    var cls = record['@class'];
    var rid = record['@rid'];
    if (!cls || !rid) return Promise.resolve(null);
    return loaders.requestWithLoader({
      loader: ctrl.loaderKey,
      method: 'GET',
      url: '/api/v1/' + ctrl.classToUrl(cls) + '/' + ridForRequest(rid)
    })
      .then(function (res) {
        if (!res) return null;
        var detail = ctrl.decorateRecord(res, { levelType: record.__levelType || 'node' }) || {};
        var children = Array.isArray(res.children) ? res.children : [];
        var normalizedChildren = children
          .filter(ctrl.filterChild)
          .map(function (child) {
            return ctrl.decorateRecord(child, {
              levelType: 'node',
              parentKey: key
            });
          });
        detail.children = normalizedChildren;
        ctrl.nodeCache[key] = detail;
        return detail;
      })
      .catch(function (err) {
        ctrl.error((err && (err.error || err.message)) || 'Failed to load record.');
        return null;
      });
  };

  ctrl.openCategory = function (category) {
    if (!category) return;
    var hosts = ctrl.hostsByClass[category.className] || [];
    var records = hosts.slice();
    ctrl.trimLevelStackTo(1);
    ctrl.pushLevel({
      type: 'category',
      label: category.label,
      category: category.className,
      records: records,
      searchFields: ['name', 'host', 'status', '@class'],
      placeholder: ctrl.attrs.hostSearchPlaceholder || ('Search ' + category.label)
    });
    m.redraw();
  };

  ctrl.openNode = function (record) {
    if (!record) return;
    ctrl.fetchNode(record)
      .then(function (detail) {
        if (!detail) return;
        var records = Array.isArray(detail.children) ? detail.children.slice() : [];
        var label = detail.name || detail.__label || ctrl.labelForClass(detail['@class']);
        var depth = ctrl.levelStack.length;
        ctrl.trimLevelStackTo(depth);
        ctrl.pushLevel({
          type: 'node',
          label: label,
          recordRid: detail['@rid'],
          records: records,
          searchFields: ['name', '@class'],
          placeholder: ctrl.attrs.nodeSearchPlaceholder || ('Search ' + label)
        });
        m.redraw();
      });
  };

  ctrl.selectRecord = function (record) {
    if (!record) return;
    var clean = ctrl.cleanForSelection(record);
    if (!clean || !clean['@rid']) return;
    ctrl.selectedNode(clean);
    m.redraw();
  };

  ctrl.shouldShowArrow = function (record) {
    if (ctrl.isCategory(record)) return true;
    if (!ctrl.isSelectable(record)) return true;
    var key = ctrl.keyForRecord(record);
    var cached = key && ctrl.nodeCache[key];
    if (cached && Array.isArray(cached.children) && cached.children.length) return true;
    return false;
  };

  ctrl.displayName = function (record) {
    if (ctrl.isCategory(record)) {
      var suffix = record.count ? ' (' + record.count + ')' : '';
      return record.label + suffix;
    }
    return record.name || record.__label || ctrl.labelForClass(record['@class']);
  };

  ctrl.onLabelClick = function (record) {
    if (ctrl.isCategory(record)) {
      ctrl.openCategory(record);
      return;
    }
    if (ctrl.attrs.selectOnLabelClick === true && ctrl.isSelectable(record)) {
      ctrl.selectRecord(record);
      return;
    }
    if (!ctrl.isSelectable(record) || ctrl.shouldShowArrow(record)) {
      ctrl.openNode(record);
      return;
    }
    if (ctrl.isSelectable(record)) {
      ctrl.selectRecord(record);
    }
  };

  ctrl.renderListItem = function (record) {
    if (!record || Object.keys(record).length === 0) return null;
    if (!ctrl.isCategory(record) && !record['@rid']) return null;

    var showArrow = ctrl.shouldShowArrow(record);
    var labelContent = m('span', [
      ctrl.displayName(record),
      showArrow ? m('i', {
        class: 'dark-blue ion-ios-arrow-right ml2 f3 b pointer',
        onclick: function (ev) {
          ev.stopPropagation();
          if (ctrl.isCategory(record)) {
            ctrl.openCategory(record);
            return;
          }
          ctrl.openNode(record);
        }
      }) : null
    ]);

    var selected = ctrl.selectedNode && ctrl.selectedNode();
    var isSelected = selected && selected['@rid'] === ensureRidString(record['@rid']);

    return m(verticalListItem, {
      label: labelContent,
      labelClass: 'dark-blue',
      onclick: function () {
        ctrl.onLabelClick(record);
      },
      onclickLabel: function () { ctrl.onLabelClick(record); }
    }, [
      ctrl.isSelectable(record) ? m('i', {
        class: 'fr f3 b ' + (isSelected ? 'ion-ios-circle-filled' : 'ion-ios-circle-outline'),
        onclick: function (ev) {
          ev.stopPropagation();
          ctrl.selectRecord(record);
        }
      }) : null
    ]);
  };

  ctrl.renderBreadcrumb = function () {
    if (!ctrl.levelStack.length) return null;
    return m('div.mb2.pb2.bb.bw1.b--mid-gray.near-white', [
      ctrl.levelStack.map(function (level, idx) {
        var label = level.label || (idx === 0 ? (ctrl.attrs.rootLabel || 'Home') : 'Level ' + idx);
        var isActive = idx === ctrl.levelStack.length - 1;
        return m('div.dib-ns.mr2.pointer', {
          class: isActive ? 'active' : 'blue',
          onclick: function () { ctrl.goToLevel(idx); }
        }, [
          label,
          (idx === ctrl.levelStack.length - 1) ? '' : m('span.near-white', ' / ')
        ]);
      })
    ]);
  };

  ctrl.loadRoot = function () {
    ctrl.loading(true);
    ctrl.error(null);
    loaders.requestWithLoader({
      loader: ctrl.loaderKey,
      method: 'GET',
      url: '/api/v1/host/'
    })
      .then(function (result) {
        ctrl.hosts = [];
        ctrl.hostsByClass = {};
        (Array.isArray(result) ? result : []).forEach(function (rec) {
          if (!rec) return;
          var decorated = ctrl.decorateRecord(rec, { levelType: 'host' });
          ctrl.hosts.push(decorated);
          var cls = decorated['@class'] || 'Unknown';
          if (!ctrl.hostsByClass[cls]) ctrl.hostsByClass[cls] = [];
          ctrl.hostsByClass[cls].push(decorated);
        });
        ctrl.levelStack = [];
        ctrl.pushLevel({
          type: 'root',
          label: ctrl.attrs.rootLabel || 'Home',
          records: ctrl.buildCategories(),
          searchFields: ['label'],
          placeholder: ctrl.attrs.categorySearchPlaceholder || 'Search network types'
        });
      })
      .catch(function (err) {
        ctrl.error((err && (err.error || err.message)) || 'Failed to load network hosts.');
        ctrl.hosts = [];
        ctrl.hostsByClass = {};
        ctrl.levelStack = [];
        ctrl.pushLevel({
          type: 'root',
          label: ctrl.attrs.rootLabel || 'Home',
          records: [],
          searchFields: ['label'],
          placeholder: ctrl.attrs.categorySearchPlaceholder || 'Search network types'
        });
      })
      .then(function () {
        ctrl.loading(false);
        m.redraw();
      });
  };

  ctrl.loadRoot();
};

deviceTree.onbeforeupdate = function (vnode) {
  this.attrs = vnode.attrs || {};
  if (vnode.attrs.selectedNode) {
    this.selectedNode = vnode.attrs.selectedNode;
  }
  if (vnode.attrs.loader) {
    this.loaderKey = vnode.attrs.loader;
  }
  return true;
};

deviceTree.view = function () {
  var ctrl = this;
  if (ctrl.loading()) {
    return m('div', [
      ctrl.attrs.hideLabel ? null : m('label', ctrl.attrs.label || 'Select network device:'),
      ctrl.attrs.hideLabel ? null : m('br'),
      m('div.f6.mid-gray', 'Loading network devices...')
    ]);
  }

  var level = ctrl.levelStack[ctrl.levelStack.length - 1] || {};
  var noData = !ctrl.currentRecords.length;

  return m('div.mb2', [
    ctrl.attrs.hideLabel ? null : m('label', ctrl.attrs.label || 'Select network device:'),
    ctrl.attrs.hideLabel ? null : m('br'),
    ctrl.renderBreadcrumb(),
    ctrl.error() ? m('div.ba.b--light-red.bg-washed-red.dark-red.pa2.br2.mb2', ctrl.error()) : null,
    noData ? m('div.f6.mid-gray.mt2', 'No records to display.') :
      m(paginationWithSearch, {
        data: ctrl.currentRecords,
        newNode: ctrl.newNode,
        dataToShow: ctrl.dataToShow,
        placeHolder: ctrl.currentPlaceholder,
        itemsPerPage: ctrl.attrs.itemsPerPage || 20,
        fieldsToSearch: ctrl.currentSearchFields
      }, [
        ctrl.dataToShow.map(function (record) {
          return ctrl.renderListItem(record);
        })
      ])
  ]);
};

module.exports = deviceTree;
